import type { StoreId, ValidationOptions } from '@subscriptions/domain/types';
import { createValueResolver } from '@subscriptions/domain/utils';
import type { PlanConcept, PlanConceptParams } from './PlanConcept';
import { createPlanConcept } from './PlanConcept';

/**
 * Store assignment within a subscription block, including plan details
 */
export type StorePlan = Readonly<{
  storeUuid: StoreId;
  storeName: string;
  plan: string;
  churnedAt: string | null;
  concepts: PlanConcept[];
}>;

/**
 * Creates a validated StorePlan value object.
 *
 * @param params - Store plan parameters
 * @param options - Validation options; with `softValidation` invalid fields degrade to empty fallbacks
 * @returns Validated StorePlan
 * @throws Error if storeUuid is empty (strict mode only)
 */
export const createStorePlan = (
  params: {
    storeUuid: string;
    storeName: string;
    plan: string;
    churnedAt?: string | null;
    concepts: PlanConceptParams[];
  },
  options?: ValidationOptions,
): StorePlan => {
  const resolve = createValueResolver(options);

  if (!options?.softValidation && !params.storeUuid?.trim()) {
    throw new Error('Store UUID is required');
  }

  return Object.freeze({
    storeUuid: params.storeUuid as StoreId,
    storeName: params.storeName,
    plan: params.plan,
    churnedAt: params.churnedAt ?? null,
    concepts: params.concepts.map((concept) =>
      resolve<PlanConcept>(
        () => createPlanConcept(concept),
        Object.freeze({
          key: '',
          value: concept.value,
          name: null,
          description: null,
          quantity: null,
          periodStart: null,
          periodEnd: null,
        }),
      ),
    ),
  });
};
